import React, { useEffect } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useIsFocused } from '@react-navigation/native';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useTheme } from '@/context/ThemeContext';
import useTranslation from '@/hooks/useTranslation';
import { useReadAloudStore } from '@/store/readAloud';
import { useModalStore } from '@/store/modal';
import { useVoiceModeStore } from '@/store/voiceMode';

const styles = StyleSheet.create({
  bar: { marginTop: 8, marginBottom: 8, borderRadius: 16, borderWidth: 1,
    flexDirection: 'row', alignItems: 'center', padding: 8, gap: 8, zIndex: 10001,
    ...Platform.select({ web: { width: '100%', alignSelf: 'center' }, default: {} }) },
  title: { flex: 1, padding: 6, minHeight: 44, justifyContent: 'center' },
  button: { width: 44, height: 44, alignItems: 'center', justifyContent: 'center' },
});

function clock(seconds) {
  const value = Math.max(0, Math.floor(seconds || 0));
  return `${Math.floor(value / 60)}:${String(value % 60).padStart(2, '0')}`;
}

export default function ReadAloudMiniPlayer({ inComposer = false }) {
  const session = useReadAloudStore(state => state.session);
  const expanded = useReadAloudStore(state => state.expanded);
  const playback = useReadAloudStore(state => state.playback);
  const controls = useReadAloudStore(state => state.controls);
  const { expand, stop } = useReadAloudStore.getState();
  const otherModalOpen = useModalStore(state => state.isOpen);
  const voiceActive = useVoiceModeStore(state => state.isActive);
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const { t } = useTranslation();
  if (!session || expanded || otherModalOpen) return null;
  return <View testID="read-aloud-mini-player" style={[styles.bar, !inComposer && {
    marginHorizontal: 12, marginBottom: Math.max(insets.bottom, 8) + 8, width: '95%', maxWidth: 520,
  }, { backgroundColor: colors.solid_color_primary, borderColor: colors.border_color }]}>
    <View style={styles.title}>
      <Text size="s" weight="medium">{t('messages.speak.title')}</Text>
      <Text size="xs" color="secondary">{playback.available
        ? `${clock(playback.position)} / ${clock(playback.duration)}`
        : playback.errorMessage || t(playback.error ? 'messages.speak.failed'
          : playback.loading ? 'messages.speak.loading' : 'messages.speak.generating')}</Text>
    </View>
    <Pressable style={styles.button} accessibilityRole="button"
      accessibilityLabel={t(playback.playing ? 'messages.speak.pause' : 'messages.speak.play')}
      disabled={!playback.available || voiceActive}
      onPress={() => playback.playing ? controls?.pause() : controls?.play()}>
      <Icon name={playback.playing ? 'pause' : 'play'} size={20} color={colors.text_color_primary} />
    </Pressable>
    <Pressable style={styles.button} accessibilityRole="button"
      accessibilityLabel={t('messages.speak.settings')} onPress={expand}>
      <Icon name="gear" size={20} color={colors.text_color_primary} />
    </Pressable>
    <Pressable style={styles.button} accessibilityRole="button"
      accessibilityLabel={t('messages.speak.stop')} onPress={stop}>
      <Icon name="xmark" size={20} color={colors.text_color_primary} />
    </Pressable>
  </View>;
}

// Only the focused route owns a composer slot. Playback stays in the root host
// when a composer is replaced or an inactive native route remains mounted.
export function ReadAloudComposerSlot() {
  const focused = useIsFocused();
  useEffect(() => {
    if (!focused) return;
    useReadAloudStore.setState(state => ({ composerSlots: state.composerSlots + 1 }));
    return () => useReadAloudStore.setState(state => ({ composerSlots: Math.max(0, state.composerSlots - 1) }));
  }, [focused]);
  return focused ? <ReadAloudMiniPlayer inComposer /> : null;
}
